/**
 * Sample an image and return a readable text + button palette for overlays.
 * Falls back to a dark-scrim / light-text palette if sampling fails (CORS, etc.).
 */
export async function getImageTonePalette(imageUrl) {
  const fallback = darkPalette();

  if (!imageUrl) return fallback;

  try {
    const img = await loadImage(imageUrl);
    const { luminance, saturation } = sampleImageStats(img);

    // Light background → dark slate text; dark/vivid → light text
    const isLight = luminance > 155 || (luminance > 135 && saturation < 0.28);

    return isLight ? lightPalette() : darkPalette();
  } catch {
    return fallback;
  }
}

function darkPalette() {
  return {
    tone: 'dark',
    textColor: '#ffffff',
    mutedColor: 'rgba(255,255,255,0.92)',
    accentColor: '#A89070',
    overlay: 'linear-gradient(90deg, rgba(15,20,24,0.78) 0%, rgba(15,20,24,0.5) 55%, rgba(15,20,24,0.22) 100%)',
    sectionOverlay: 'linear-gradient(180deg, rgba(15,20,24,0.82) 0%, rgba(15,20,24,0.48) 38%, rgba(15,20,24,0.22) 72%, rgba(15,20,24,0.1) 100%)',
    shadow: '0 2px 18px rgba(0,0,0,0.55)',
    // Solid light CTA + clear outline secondary on dark photos
    btnPrimaryBg: '#ffffff',
    btnPrimaryText: '#384858',
    btnPrimaryBorder: '#ffffff',
    btnPrimaryHoverBg: '#A89070',
    btnPrimaryHoverText: '#ffffff',
    btnSecondaryBg: 'transparent',
    btnSecondaryText: '#ffffff',
    btnSecondaryBorder: 'rgba(255,255,255,0.9)',
    btnSecondaryHoverBg: '#ffffff',
    btnSecondaryHoverText: '#384858',
  };
}

function lightPalette() {
  return {
    tone: 'light',
    textColor: '#384858',
    mutedColor: 'rgba(56,72,88,0.9)',
    accentColor: '#707850',
    overlay: 'linear-gradient(90deg, rgba(244,241,234,0.82) 0%, rgba(244,241,234,0.48) 55%, rgba(244,241,234,0.14) 100%)',
    sectionOverlay: 'linear-gradient(180deg, rgba(244,241,234,0.9) 0%, rgba(244,241,234,0.55) 36%, rgba(244,241,234,0.18) 70%, rgba(244,241,234,0.08) 100%)',
    shadow: '0 2px 14px rgba(255,255,255,0.4)',
    // Solid slate CTA + outlined secondary on light photos
    btnPrimaryBg: '#384858',
    btnPrimaryText: '#ffffff',
    btnPrimaryBorder: '#384858',
    btnPrimaryHoverBg: '#707850',
    btnPrimaryHoverText: '#ffffff',
    btnSecondaryBg: 'transparent',
    btnSecondaryText: '#384858',
    btnSecondaryBorder: '#384858',
    btnSecondaryHoverBg: '#384858',
    btnSecondaryHoverText: '#ffffff',
  };
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('image load failed'));
    img.src = src;
  });
}

function sampleImageStats(img) {
  const canvas = document.createElement('canvas');
  const w = 64;
  const h = 36;
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(img, 0, 0, w, h);

  // Focus on left/center where titles and CTAs usually sit
  const x0 = 0;
  const x1 = Math.floor(w * 0.62);
  const y0 = Math.floor(h * 0.2);
  const y1 = Math.floor(h * 0.9);
  const data = ctx.getImageData(x0, y0, x1 - x0, y1 - y0).data;

  let lumSum = 0;
  let satSum = 0;
  let count = 0;
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const a = data[i + 3];
    if (a < 20) continue;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    const sat = max === 0 ? 0 : (max - min) / max;
    lumSum += lum;
    satSum += sat;
    count += 1;
  }

  return {
    luminance: count ? lumSum / count : 80,
    saturation: count ? satSum / count : 0.3,
  };
}

export default getImageTonePalette;
